import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Swords, Eye, Radio, Search, AlertTriangle } from 'lucide-react';
import { fmt, fmtDate, gbErr, FLAG_LABEL } from './gbAdminUtils';

const CAUSA = {
  destruido: 'nocaute', tempo: 'tempo esgotado', desistiu: 'desistência',
  precipicio: 'caiu no abismo', afk: 'abandonou (AFK)',
};

const Caixa = ({ label, valor, tom = 'text-slate-700', testid }) => (
  <div className="rounded-xl border p-3" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`text-base font-black ${tom}`}>{valor}</p>
  </div>
);

const Barra = ({ hp, max }) => (
  <span className="inline-block h-1.5 w-14 overflow-hidden rounded-full bg-slate-200 align-middle">
    <span className="block h-full rounded-full bg-emerald-500"
      style={{ width: `${Math.max(0, Math.min(100, (hp / (max || 300)) * 100))}%` }} />
  </span>
);

export default function GbPvpMatches({ seasonId }) {
  const [live, setLive] = useState(null);
  const [rows, setRows] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [soMarcadas, setSoMarcadas] = useState(false);
  const timer = useRef(null);

  const loadLive = useCallback(async () => {
    try { const { data } = await api.get('/admin/gb/pvp/live'); setLive(data); } catch (e) { /* silencioso */ }
  }, []);

  const loadHist = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/gb/season/${seasonId}/pvp/matches`,
        { params: { limit: 200, flagged: soMarcadas, ...(busca.trim() ? { kako_id: busca.trim() } : {}) } });
      setRows(data.matches || []);
      setResumo(data.summary || null);
    } catch (e) { toast.error(gbErr(e)); } finally { setLoading(false); }
  }, [seasonId, soMarcadas, busca]);

  useEffect(() => { loadHist(); }, [loadHist]);

  useEffect(() => {                       // ao vivo: atualiza a cada 5s
    loadLive();
    timer.current = setInterval(loadLive, 5000);
    return () => clearInterval(timer.current);
  }, [loadLive]);

  return (
    <div className="space-y-4" data-testid="gb-admin-pvp">
      <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-black uppercase text-rose-700">
          <Radio className="h-3.5 w-3.5 animate-pulse" /> Ao vivo agora
          <span className="ml-auto text-[10px] font-bold normal-case text-rose-400">atualiza a cada 5s</span>
        </p>
        <div className="grid gap-2 sm:grid-cols-4">
          <Caixa label="Conectados" valor={fmt(live?.online)} testid="gb-pvp-live-online" />
          <Caixa label="Na sala de combinação" valor={fmt(live?.room)} testid="gb-pvp-live-room" />
          <Caixa label="Procurando oponente" valor={fmt(live?.queue)} testid="gb-pvp-live-queue" />
          <Caixa label="Duelos rolando" valor={fmt(live?.matches?.length)} tom="text-rose-700"
            testid="gb-pvp-live-matches" />
        </div>

        {(live?.matches || []).length > 0 && (
          <div className="mt-2 space-y-2" data-testid="gb-pvp-live-list">
            {live.matches.map((m) => (
              <div key={m.match_id} className="rounded-xl border bg-white p-2.5 text-xs"
                data-testid={`gb-pvp-live-${m.match_id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {['a', 'b'].map((l) => (
                    <span key={l} className="flex items-center gap-1.5">
                      {m.turn === l && <span className="h-2 w-2 rounded-full bg-emerald-500" title="é a vez dele" />}
                      <b className="text-slate-700">{m[l].nickname}</b>
                      <span className="font-mono text-[10px] text-slate-400">{m[l].kako_id}</span>
                      <Barra hp={m[l].hp} max={m.max_hp} />
                      <span className="font-bold text-slate-500">{m[l].hp}</span>
                      {m[l].afk && <span className="rounded-full bg-amber-100 px-1.5 font-bold text-amber-700">AFK</span>}
                      {l === 'a' && <span className="px-1 font-black text-rose-400">VS</span>}
                    </span>
                  ))}
                  <span className="ml-auto flex items-center gap-2 text-[10px] font-bold text-slate-500">
                    <span>turno {m.index}</span>
                    <span>{Math.floor(Math.max(0, m.match_seconds - m.seconds) / 60)}:
                      {String(Math.max(0, m.match_seconds - m.seconds) % 60).padStart(2, '0')} restantes</span>
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{m.spectators}</span>
                    <span className="text-amber-600">entrada {fmt(m.entry)}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {(live?.room_players || []).length > 0 && (
          <p className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-slate-500"
            data-testid="gb-pvp-live-roomlist">
            <b className="uppercase text-slate-400">Na sala:</b>
            {live.room_players.map((q) => (
              <span key={q.kako_id} className={`rounded-full border px-2 py-0.5 font-bold ${
                q.in_match ? 'bg-slate-100 text-slate-400' : q.searching ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'text-slate-600'}`}>
                {q.nickname}{q.searching ? ' · procurando' : q.in_match ? ' · em duelo' : ''}
              </span>
            ))}
          </p>
        )}
        {live && live.ok === false && (
          <p className="mt-2 text-[11px] font-bold text-amber-700">
            Arena ao vivo indisponível no servidor (nenhuma conexão ativa).
          </p>
        )}
      </div>

      {resumo && (
        <div className="grid gap-2 sm:grid-cols-5" data-testid="gb-pvp-summary">
          <Caixa label="Duelos no evento" valor={fmt(resumo.duels)} testid="gb-pvp-sum-duels" />
          <Caixa label="Moedas arrecadadas" valor={fmt(resumo.collected)} tom="text-amber-600"
            testid="gb-pvp-sum-collected" />
          <Caixa label="Prêmios pagos" valor={fmt(resumo.prizes)} tom="text-emerald-700"
            testid="gb-pvp-sum-prizes" />
          <Caixa label="Banca" valor={fmt(resumo.house)} tom="text-slate-700" testid="gb-pvp-sum-house" />
          <Caixa label="Jackpot acumulado" valor={fmt(resumo.jackpot)} tom="text-purple-700"
            testid="gb-pvp-sum-jackpot" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadHist()}
            placeholder="filtrar por KAKO ID" data-testid="gb-pvp-filter-kako"
            className="w-48 rounded-lg border py-1.5 pl-7 pr-2 text-xs outline-none focus:border-orange-400" />
        </span>
        <button onClick={() => setSoMarcadas((v) => !v)} data-testid="gb-pvp-filter-flagged"
          className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-bold ${
            soMarcadas ? 'border-amber-400 bg-amber-50 text-amber-700' : 'text-slate-500'}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> só marcadas
        </button>
        <button onClick={loadHist} data-testid="gb-pvp-refresh"
          className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> atualizar
        </button>
        <span className="ml-auto flex items-center gap-1.5 text-xs font-black uppercase text-slate-500">
          <Swords className="h-3.5 w-3.5" /> últimos duelos · {rows.length}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border p-4 text-center text-xs text-slate-400" data-testid="gb-pvp-empty">
          Nenhum duelo registrado{soMarcadas ? ' com marcação' : ''}{busca ? ` para ${busca}` : ''}.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                {['Quando', 'Vencedor', 'Perdedor', 'Fim', 'Vida', 'XP', 'Entrada', 'Prêmio', 'Banca', 'Jackpot', 'Marcações'].map((h) => (
                  <th key={h} className="px-2 py-2 text-left font-black">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.match_id} className="border-t" data-testid={`gb-pvp-match-${d.match_id}`}>
                  <td className="px-2 py-1.5 text-slate-500">{fmtDate(d.at)}</td>
                  <td className="px-2 py-1.5">
                    <b className="text-emerald-700">{d.winner?.nickname || '—'}</b>
                    <span className="ml-1 font-mono text-[10px] text-slate-400">{d.winner?.kako_id}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    <b className="text-slate-600">{d.loser?.nickname || '—'}</b>
                    <span className="ml-1 font-mono text-[10px] text-slate-400">{d.loser?.kako_id}</span>
                  </td>
                  <td className="px-2 py-1.5 text-slate-500">{CAUSA[d.cause] || d.cause || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-500">
                    {d.winner?.hp_left ?? '—'} <span className="text-slate-300">×</span> {d.loser?.hp_left ?? '—'}
                  </td>
                  <td className="px-2 py-1.5 font-bold text-cyan-700">
                    +{fmt(d.winner?.xp)} <span className="text-slate-300">/</span> +{fmt(d.loser?.xp)}
                  </td>
                  <td className="px-2 py-1.5 text-amber-600">{fmt(d.entry)}</td>
                  <td className="px-2 py-1.5 font-black text-emerald-700">{fmt(d.prize)}</td>
                  <td className="px-2 py-1.5 text-slate-500">{fmt(d.house)}</td>
                  <td className="px-2 py-1.5 text-purple-700">{fmt(d.jackpot)}</td>
                  <td className="px-2 py-1.5">
                    <span className="flex flex-wrap gap-1">
                      {(d.flags || []).map((f) => (
                        <span key={f} className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                          {FLAG_LABEL[f] || f}
                        </span>
                      ))}
                      {d.refund > 0 && (
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700">
                          devolvido {fmt(d.refund)}
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
